import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { SecurityService } from '@core/services';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { Mapper } from '@shared/helpers/mapper';
import { Context } from '@shared/helpers/mapper/abstractions/context';
import {
  Security,
  SecurityQuestionAnswer,
  SecurityReview,
  SecurityTaxResultItem,
  TaxResultItemType,
} from '@shared/models/security';
import { SecurityReviewResult } from '@shared/models/security/security-review-result';
import { QueryOptions } from 'odata-query';
import { combineLatest, map, Observable, switchMap, tap } from 'rxjs';
import { ApproveSecurityRequest } from 'src/app/web-api-client';
import { mapAccordionToApproveRequest } from '../../admin/mappers';
import { Review } from '../abstractions/review';
import { SecurityMetadata } from '../abstractions/security-metadata';
import { SecurityReviewEditionCache } from '../abstractions/security-review-edition-cache';
import { AccordionSecurityReview } from '../models/edit-tax-result-approval/accordion-security-review';
import { SecurityTaxResultItemForm } from '../models/edit-tax-result-approval/security-tax-result-item-form';
import { SecurityTaxResultItemReview } from '../models/edit-tax-result-approval/security-tax-result-item-review';
import { TaxResultItemField } from '../models/edit-tax-result-approval/tax-result-item-field';
import {
  SecurityTypeResult,
  TaxResultItemTypeResult,
} from '../models/security-type-result';
import { TaxResultEditionService } from './tax-result-edition.service';
import { TaxResultService } from './tax-result.service';

@Injectable({
  providedIn: 'root',
})
export class TaxResultApprovalService {
  private readonly securityService = inject(SecurityService);
  private readonly taxResultService = inject(TaxResultService);
  private readonly taxResultEditionService = inject(TaxResultEditionService);

  isEditingSecuritiesRules: WritableSignal<boolean> = signal(false);

  cachedSecuritiesForTaxResultEdition: WritableSignal<
    AccordionSecurityReview[]
  > = signal([]);
  securitySelectedForRuleEdition: WritableSignal<AccordionSecurityReview | null> =
    signal(null);
  ratesIdToDelete: WritableSignal<number[]> = signal([]);

  setSecuritiesForRuleEdition(
    securitiesNotEdited: AccordionSecurityReview[],
    securityForEdition: AccordionSecurityReview
  ): void {
    this.isEditingSecuritiesRules.set(true);
    this.cachedSecuritiesForTaxResultEdition.set(securitiesNotEdited);
    this.securitySelectedForRuleEdition.set(securityForEdition);
  }

  loadSecuritiesWithPendingApproval(
    ids: number[]
  ): Observable<AccordionSecurityReview[]> {
    const query: Partial<QueryOptions<Security>> = {
      filter: `securityId in (${ids.join(',')})`,
    };

    return combineLatest({
      securities: this.securityService.reviewWithAnswers({
        ...query,
        count: true,
      }),
      typeResults: this.taxResultService.getTypesResult(),
    }).pipe(
      tap(({ securities }: Review) => this.resolveEmptyExplanation(securities)),
      map(({ securities, typeResults }: Review): AccordionSecurityReview[] =>
        this.buildAccordionItems(securities, typeResults)
      )
    );
  }

  private buildAccordionItems(
    securities: SecurityReview[],
    typeResults: SecurityTypeResult[]
  ): AccordionSecurityReview[] {
    return Mapper.mapByList(securities, AccordionSecurityReview, [
      {
        title: (source: SecurityReview) => source.name,
        isExpanded: (_source, _destiny, context) => context.isFirst,
        security: (source: SecurityReview) => source,
        initialComment: source => source.comment ?? '',
        comment: (source: SecurityReview) => signal(source.comment ?? ''),
        initialAdminComment: source => source.adminComment ?? '',
        adminComment: (source: SecurityReview) =>
          signal(source.adminComment ?? ''),
        securityTaxResultItems: (
          source: SecurityReview,
          _target,
          context: Context
        ): SecurityTaxResultItemReview[] => {
          context.cache['security-metadata'] = {
            securityId: source.securityId,
            transactionTypeId: source.transactionType.transactionTypeId,
          };

          if (
            !source.securityQuestionAnswers.length &&
            !source.securityTaxResultItems.length
          ) {
            const taxResultArray = this.typeResultBuilder(
              typeResults,
              source.securityType.securityTypeId
            );
            return this.buildSecurityTaxResultItemForm(taxResultArray, []);
          }
          return this.buildSecurityTaxResultItemForm(
            source.securityTaxResultItems,
            source.securityQuestionAnswers
          );
        },
      },
    ]);
  }

  approveSecurities(
    accordionSecuritiesReviewList: AccordionSecurityReview[],
    cache: SecurityReviewEditionCache
  ): Observable<SecurityReviewResult[]> {
    const approveSecurityRequest: ApproveSecurityRequest[] =
      mapAccordionToApproveRequest(accordionSecuritiesReviewList);
    const securitiesToUpdate: Security[] = this.getSecurities(
      accordionSecuritiesReviewList
    );
    return this.securityService
      .approve(approveSecurityRequest)
      .pipe(
        switchMap((securities: SecurityReviewResult[]) =>
          this.taxResultEditionService.analyzeResponse(
            securities,
            securitiesToUpdate,
            cache
          )
        )
      );
  }

  private getSecurities(
    accordionSecuritiesReviewList: AccordionSecurityReview[]
  ): Security[] {
    return accordionSecuritiesReviewList.map(
      (accordionSecurityReview: AccordionSecurityReview) =>
        accordionSecurityReview.security
    );
  }

  private sortQuestionsByTaxResultItem(
    securitiesQuestionAnswer: SecurityQuestionAnswer[],
    securityTaxResultItem: SecurityTaxResultItem,
    metadata: SecurityMetadata
  ): SecurityTaxResultItemForm[] {
    const questionsAnswer: SecurityQuestionAnswer[] =
      securitiesQuestionAnswer?.filter(
        (securityQuestion: SecurityQuestionAnswer): boolean =>
          !!(
            securityQuestion.taxPolicy &&
            securityQuestion.taxPolicy.taxResultItemType
              .taxResultItemTypeId! ===
              securityTaxResultItem.taxResultItemTypeId
          )
      );
    if (questionsAnswer.length > 0) {
      return Mapper.mapByList(questionsAnswer, SecurityTaxResultItemForm, [
        {
          questionText: source => source.question.text,
          yesNoValue: source => (source.unsureAnswer ? 'unsure' : source.text),
          explanation: (_source, _destiny, context) =>
            !context.isLast
              ? undefined
              : Mapper.map(securityTaxResultItem, TaxResultItemField, [
                  {
                    value: (
                      source: SecurityTaxResultItem
                    ): WritableSignal<string> => {
                      return signal(
                        source.securityTaxResultItemDescription === '' &&
                          source.taxResultItemDescription !== ''
                          ? source.taxResultItemDescription
                          : source.securityTaxResultItemDescription
                      );
                    },
                    uuid: crypto.randomUUID(),
                    metadata,
                    initialValue: source =>
                      `${
                        source.securityTaxResultItemDescription === '' &&
                        source.taxResultItemDescription !== ''
                          ? source.taxResultItemDescription
                          : source.securityTaxResultItemDescription
                      }`,
                    ignoreValidation: (source): boolean =>
                      (source.securityTaxResultItemDescription === '' &&
                      source.taxResultItemDescription !== ''
                        ? source.taxResultItemDescription
                        : source.securityTaxResultItemDescription) === '' &&
                      <TransactionType>metadata.transactionTypeId ===
                        TransactionType.GeneralRates,
                  },
                ]),
          rate: (
            _source,
            _destiny,
            context
          ): TaxResultItemField | undefined => {
            return !context.isLast
              ? undefined
              : Mapper.map(securityTaxResultItem, TaxResultItemField, [
                  {
                    value: (
                      source: SecurityTaxResultItem
                    ): WritableSignal<string> => {
                      const rate =
                        source.securityTaxResultItemRate === null &&
                        source.taxResultItemRate !== null
                          ? source.taxResultItemRate
                          : source.securityTaxResultItemRate;
                      return signal(
                        rate !== null && rate !== undefined && rate.toString()
                          ? `${rate}%`
                          : ''
                      );
                    },
                    uuid: crypto.randomUUID(),
                    metadata,
                    initialValue: source =>
                      `${
                        source.securityTaxResultItemRate === null &&
                        source.taxResultItemRate !== null
                          ? source.taxResultItemRate
                          : source.securityTaxResultItemRate
                      }%`,
                  },
                ]);
          },
          isUnsure: source => source.unsureAnswer,
          metadata,
          uuid: crypto.randomUUID(),
        },
      ]);
    }

    return Mapper.mapByList(
      [securityTaxResultItem],
      SecurityTaxResultItemForm,
      [
        {
          explanation: (_source, _destiny, context) =>
            !context.isLast
              ? undefined
              : Mapper.map(securityTaxResultItem, TaxResultItemField, [
                  {
                    value: (
                      source: SecurityTaxResultItem
                    ): WritableSignal<string> => {
                      return signal(
                        source.securityTaxResultItemDescription === '' &&
                          source.taxResultItemDescription !== ''
                          ? source.taxResultItemDescription
                          : source.securityTaxResultItemDescription
                      );
                    },
                    uuid: crypto.randomUUID(),
                    metadata,
                    initialValue: ({
                      securityTaxResultItemDescription,
                      taxResultItemDescription,
                    }: SecurityTaxResultItem): string => {
                      if (securityTaxResultItemDescription) {
                        return securityTaxResultItemDescription;
                      } else if (taxResultItemDescription) {
                        return taxResultItemDescription;
                      }

                      return '';
                    },
                    ignoreValidation: (source): boolean =>
                      (source.securityTaxResultItemDescription === '' &&
                      source.taxResultItemDescription !== ''
                        ? source.taxResultItemDescription
                        : source.securityTaxResultItemDescription) === '' &&
                      <TransactionType>metadata.transactionTypeId ===
                        TransactionType.GeneralRates,
                  },
                ]),
          rate: (_source, _destiny, context) =>
            !context.isLast
              ? undefined
              : Mapper.map(securityTaxResultItem, TaxResultItemField, [
                  {
                    value: ({
                      securityTaxResultItemRate,
                      taxResultItemRate,
                    }: SecurityTaxResultItem): WritableSignal<string> => {
                      if (typeof securityTaxResultItemRate === 'number') {
                        return signal(`${securityTaxResultItemRate}%`);
                      } else if (typeof taxResultItemRate === 'number') {
                        return signal(`${taxResultItemRate}%`);
                      }

                      return signal('');
                    },
                    uuid: crypto.randomUUID(),
                    metadata,
                    initialValue: ({
                      securityTaxResultItemRate,
                      taxResultItemRate,
                    }: SecurityTaxResultItem): string => {
                      if (typeof securityTaxResultItemRate === 'number') {
                        return `${securityTaxResultItemRate}%`;
                      } else if (typeof taxResultItemRate === 'number') {
                        return `${taxResultItemRate}%`;
                      }

                      return '';
                    },
                  },
                ]),
          metadata,
          uuid: crypto.randomUUID(),
        },
      ]
    );
  }

  private buildSecurityTaxResultItemForm(
    securityTaxResultItems: SecurityTaxResultItem[],
    securityQuestionAnswers: SecurityQuestionAnswer[]
  ): SecurityTaxResultItemReview[] {
    const itemsReview = Mapper.mapByList(
      securityTaxResultItems,
      SecurityTaxResultItemReview,
      [
        {
          securityTaxResultItemId: (source: SecurityTaxResultItem): number =>
            source.securityTaxResultItemId,
          taxResultTypeId: (source: SecurityTaxResultItem): number =>
            source.taxResultItemTypeId,
          title: (source: SecurityTaxResultItem): string =>
            source.taxResultItemType.name,
          items: (
            source: SecurityTaxResultItem,
            _target,
            context
          ): WritableSignal<SecurityTaxResultItemForm[]> => {
            const metadata = {
              ...(<SecurityMetadata>context.cache['security-metadata']),
            };
            metadata.securityTaxResultItemId = source.securityTaxResultItemId;
            metadata.taxResultItemTypeId = source.taxResultItemTypeId;
            return signal(
              this.sortQuestionsByTaxResultItem(
                securityQuestionAnswers,
                source,
                metadata
              )
            );
          },
        },
      ]
    );

    itemsReview.forEach(
      itemReview => (itemReview.initialSize = itemReview.items().length)
    );

    return itemsReview;
  }

  private typeResultBuilder(
    typeResults: SecurityTypeResult[],
    securityTypeId: number
  ): SecurityTaxResultItem[] {
    const typeResult: SecurityTypeResult | undefined = typeResults.find(
      (result: SecurityTypeResult) => result.securityTypeId === securityTypeId
    );

    if (typeResult && typeResult!.taxResultItemTypeResults) {
      return typeResult!.taxResultItemTypeResults.map(
        (taxResult: TaxResultItemTypeResult) => {
          const item: SecurityTaxResultItem = new SecurityTaxResultItem();
          item.taxResultItemTypeId = taxResult.taxResultItemTypeId ?? 0;
          item.taxResultItemType = new TaxResultItemType();
          item.taxResultItemType.taxResultItemTypeId =
            taxResult.taxResultItemTypeId ?? 0;
          item.taxResultItemType.name = taxResult.name ?? '';
          return item;
        }
      );
    }
    return [];
  }

  sortItemsByRate(array: SecurityTaxResultItem[]): SecurityTaxResultItem[] {
    return array.sort(
      (a, b) =>
        a.taxResultItemTypeId - b.taxResultItemTypeId ||
        (a.taxResultItemRate ?? 0) - (b.taxResultItemRate ?? 0)
    );
  }

  private resolveEmptyExplanation(securities: SecurityReview[]): void {
    securities.forEach((security: SecurityReview) => {
      if (
        security.transactionType.transactionTypeId ===
        TransactionType.GeneralRates
      ) {
        const groups = security.securityTaxResultItems.reduce(
          (result, item) => {
            if (result[item.taxResultItemType.name]) {
              const list = result[item.taxResultItemType.name];
              list.push(item);
              result[item.taxResultItemType.name] = list;
            } else {
              result[item.taxResultItemType.name] = [item];
            }

            return result;
          },
          {} as Record<string, SecurityTaxResultItem[]>
        );

        Object.values(groups).forEach((items: SecurityTaxResultItem[]) => {
          const explanation = items.find(
            item =>
              item.securityTaxResultItemDescription ||
              item.taxResultItemDescription
          )!;
          items.forEach(item => {
            item.taxResultItemDescription =
              explanation?.taxResultItemDescription;
            item.securityTaxResultItemDescription =
              explanation?.securityTaxResultItemDescription;
          });
        });
      }
    });
  }
}
